import { QuickFillItem } from '../../types/domain/quick-fill.types';
import { ETALON_END_TYPE_OPTIONS, getEtalonObjectTypes } from '../../config/pcfEtalonCatalog';

export interface ValidationError {
  field: string;
  message: string;
}

export interface FieldValidationResult {
  isValid: boolean;
  error?: string;
}

// Validation rules
const SKEY_PATTERN = /^[A-Z0-9?]{3,}$/;
const VALID_OBJECT_TYPES = getEtalonObjectTypes();
const VALID_END_TYPES = ETALON_END_TYPE_OPTIONS.map((option) => option.value.toUpperCase());
const END_TYPE_FIELDS = ['End1', 'End2', 'End3', 'End4'];

export function validateSKEY(value: string): FieldValidationResult {
  if (!value) return { isValid: true }; // Optional field
  if (!SKEY_PATTERN.test(value)) {
    return {
      isValid: false,
      error: 'SKEY must be 3+ uppercase letters, numbers, or ?'
    };
  }
  return { isValid: true };
}

export function validateObjectType(value: string): FieldValidationResult {
  if (!value) return { isValid: true }; // Optional field
  if (!VALID_OBJECT_TYPES.includes(value)) {
    return {
      isValid: false,
      error: `Must be one of: ${VALID_OBJECT_TYPES.slice(0, 5).join(', ')}`
    };
  }
  return { isValid: true };
}

export function validateEndType(value: string): FieldValidationResult {
  if (!value) return { isValid: true }; // Optional field

  const normalizedValue = value.trim().toUpperCase();
  if (!VALID_END_TYPES.includes(normalizedValue)) {
    return {
      isValid: false,
      error: `END-TYPE must be one of: ${VALID_END_TYPES.slice(0, 5).join(', ')}`
    };
  }

  return { isValid: true };
}

export function validateRequired(value: string | undefined, fieldName: string): FieldValidationResult {
  if (!value || value.trim() === '') {
    return {
      isValid: false,
      error: `${fieldName} is required`
    };
  }
  return { isValid: true };
}

// Data type validation for different parameter types
export function validateDataType(value: string, dataType: string): FieldValidationResult {
  if (!value || value.trim() === '') {
    return { isValid: true }; // Empty is valid (optional)
  }

  switch (dataType?.toLowerCase()) {
    case 'integer':
    case 'int':
      if (!/^-?\d+$/.test(value)) {
        return { isValid: false, error: 'Must be a whole number (e.g., 42, -10)' };
      }
      break;
    case 'number':
    case 'double':
    case 'real':
    case 'float':
      if (!/^-?\d*\.?\d+$/.test(value)) {
        return { isValid: false, error: 'Must be a number (e.g., 3.14, -10.5)' };
      }
      break;
    case 'length':
    case 'area':
    case 'volume':
      // Allow numbers with optional units
      if (!/^-?\d*\.?\d+\s*(mm|cm|m|ft|in|'|")?$/.test(value)) {
        return { isValid: false, error: 'Must be a measurement (e.g., 100, 2.5m, 10ft)' };
      }
      break;
    case 'angle':
      // Allow numbers with optional degree symbol
      if (!/^-?\d*\.?\d+\s*°?$/.test(value)) {
        return { isValid: false, error: 'Must be an angle (e.g., 90, 45.5°)' };
      }
      break;
    case 'yesno':
    case 'boolean':
      const boolValues = ['yes', 'no', 'true', 'false', '1', '0'];
      if (!boolValues.includes(value.toLowerCase())) {
        return { isValid: false, error: 'Must be Yes/No or True/False' };
      }
      break;
    // String types - no validation needed
    case 'string':
    case 'text':
    default:
      break;
  }

  return { isValid: true };
}

export function validateField(field: keyof QuickFillItem, value: string, dataType?: string): FieldValidationResult {
  switch (field) {
    case 'Name':
      return validateRequired(value, 'Name');
    case 'SKEY':
      return validateSKEY(value);
    case 'OBJECT-TYPE':
      return validateObjectType(value);
    case 'End1':
    case 'End2':
    case 'End3':
    case 'End4':
      return validateEndType(value);
    default:
      // If dataType is provided, validate against it
      if (dataType) {
        return validateDataType(value, dataType);
      }
      return { isValid: true };
  }
}

export function validateQuickFillItem(item: QuickFillItem): ValidationError[] {
  const errors: ValidationError[] = [];

  // Validate Name (required)
  const nameResult = validateRequired(item.Name, 'Name');
  if (!nameResult.isValid) {
    errors.push({ field: 'Name', message: nameResult.error! });
  }

  // Validate SKEY
  if (item.SKEY) {
    const skeyResult = validateSKEY(item.SKEY);
    if (!skeyResult.isValid) {
      errors.push({ field: 'SKEY', message: skeyResult.error! });
    }
  }

  // Validate OBJECT-TYPE
  if (item['OBJECT-TYPE']) {
    const objectTypeResult = validateObjectType(item['OBJECT-TYPE']);
    if (!objectTypeResult.isValid) {
      errors.push({ field: 'OBJECT-TYPE', message: objectTypeResult.error! });
    }
  }

  // Validate END-TYPE slots
  END_TYPE_FIELDS.forEach((field) => {
    const value = item[field];
    if (!value) {
      return;
    }

    const endTypeResult = validateEndType(value);
    if (!endTypeResult.isValid) {
      errors.push({ field, message: endTypeResult.error! });
    }
  });

  return errors;
}
